import React, { CSSProperties, FC, useState } from "react";
import Select, { StylesConfig } from "react-select";
import makeAnimated from "react-select/animated";
import { IInputProps } from "../../../modules/form";
import chroma from "chroma-js";

interface IAsyncDataConfig {
  pathLabel: string;
  pathValue: string;
  response: any;
}

interface AreaOption {
  value: string;
  label: string;
  color: string;
  isDisabled?: boolean;
}
const animatedComponents = makeAnimated();

const AreaOneOptions: AreaOption[] = [
  { value: "1-A", label: "1-A", color: "#0b95e6", isDisabled: false },
  { value: "1-B", label: "1-B", color: "#0b95e6", isDisabled: false },
  { value: "1-C", label: "1-C", color: "#0b95e6", isDisabled: false },
  { value: "1-D", label: "1-D", color: "#0b95e6", isDisabled: false },
  { value: "1", label: "1", color: "#000", isDisabled: false },
  { value: "1-Đỏ", label: "1", color: "#FF0000", isDisabled: false },
];

const AreaTwoOptions: AreaOption[] = [
  { value: "2", label: "2", color: "#0b95e6", isDisabled: false },
  { value: "2-A", label: "2-A", color: "#0b95e6", isDisabled: false },
  { value: "2-C", label: "2-C", color: "#0b95e6", isDisabled: false },
];

const AreaThreeOptions: AreaOption[] = [
  { value: "3", label: "3", color: "#0b95e6", isDisabled: false },
  { value: "3-A", label: "3-A", color: "#0b95e6", isDisabled: false },
  { value: "3-B", label: "3-B", color: "#0b95e6", isDisabled: false },
  { value: "3-C", label: "3-C", color: "#0b95e6", isDisabled: false },
];

const AreaFourOptions: AreaOption[] = [
  { value: "4", label: "4", color: "#000", isDisabled: false },
  { value: "4-Đỏ", label: "4", color: "#FF0000", isDisabled: false },
];

const AreaFiveOptions: AreaOption[] = [
  { value: "5", label: "5", color: "#0b95e6", isDisabled: false },
  { value: "5-A", label: "5-A", color: "#0b95e6", isDisabled: false },
  { value: "5-B", label: "5-B", color: "#0b95e6", isDisabled: false },
  { value: "5-C", label: "5-C", color: "#0b95e6", isDisabled: false },
  { value: "5-D", label: "5-D", color: "#0b95e6", isDisabled: false },
  { value: "5-E", label: "5-E", color: "#0b95e6", isDisabled: false },
];

const AreaSixOptions: AreaOption[] = [
  { value: "6", label: "6", color: "#0b95e6", isDisabled: false },
  { value: "6-A", label: "6-A", color: "#0b95e6", isDisabled: false },
  { value: "6-B", label: "6-B", color: "#0b95e6", isDisabled: false },
  { value: "6-C", label: "6-C", color: "#0b95e6", isDisabled: false },
];

interface IInputSelect extends IInputProps {
  options?: any;
  isClearable?: boolean;
  asyncData?: IAsyncDataConfig;
  placeholder?: string;
}

interface GroupedOption {
  label: string;
  options: AreaOption[];
}

export const InputGroupSelect: FC<IInputSelect> = (props) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const updatedAreaFourOptions = AreaFourOptions.map((option) => {
    if (option.value === "4-Đỏ") {
      option.isDisabled = selectedValue === "4";
    } else if (option.value === "4") {
      option.isDisabled = selectedValue === "4-Đỏ";
    }
    return option;
  });

  const groupedOptions: GroupedOption[] = [
    {
      label: "1",
      options: AreaOneOptions,
    },
    {
      label: "2",
      options: AreaTwoOptions,
    },
    {
      label: "3",
      options: AreaThreeOptions,
    },
    {
      label: "4",
      options: updatedAreaFourOptions,
    },
    {
      label: "5",
      options: AreaFiveOptions,
    },
    {
      label: "6",
      options: AreaSixOptions,
    },
  ];

  const handleChange = (data: any) => {
    // Check if the selected option is disabled
    const updatedData = data?.filter((option: any) => {
      const selectedOption = groupedOptions.find((group) => group.options.find((opt) => opt.value === option.value));
      return !selectedOption?.options.find((opt) => opt.value === option.value)?.isDisabled;
    });

    // Update the selected value
    const selected = updatedData ? updatedData.map((item: any) => item.value) : [];
    setSelectedValue(selected.includes("4") ? "4" : selected.includes("4-Đỏ") ? "4-Đỏ" : null);

    // Call the onChange prop with the updated data
    props.onChange(updatedData);
  };

  const generalProps = {
    className: "InputSelect",
    classNamePrefix: "InputSelect",
    isSearchable: true,
    placeholder: props.placeholder || "-- Select --",
    onChange: handleChange,
    defaultValue: props.defaultValue,
    onBlur: () => props.onTouched(),
    isClearable: typeof props.isClearable === "boolean" ? props.isClearable : true,
    isDisabled: props.isDisabled,
    isMulti: true,
  };

  const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#333",
  };

  const colourStyles: StylesConfig<AreaOption, true> = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      const color = chroma(data.color);
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? data.color
          : isFocused
          ? color.alpha(0.1).css()
          : undefined,
        color: isDisabled
          ? "#000"
          : isSelected
          ? chroma.contrast(color, "white") > 2
            ? "white"
            : "black"
          : data.color,
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...(styles[":active" as keyof CSSProperties] as CSSProperties),
          backgroundColor: !isDisabled ? (isSelected ? data.color : color.alpha(0.3).css()) : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      const color = chroma(data.color);
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data.color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data.color,
      ":hover": {
        backgroundColor: data.color,
        color: "white",
      },
    }),
  };

  const groupBadgeStyles: CSSProperties = {
    backgroundColor: "#EBECF0",
    borderRadius: "2em",
    color: "#172B4D",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
  };

  const formatGroupLabel = (data: any) => (
    <div style={groupStyles}>
      <span onClick={() => handleSelectAll(data)}>Khu vực {data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );

  const handleSelectAll = (data: any) => {};

  return (
    <Select
      {...generalProps}
      options={groupedOptions}
      components={animatedComponents}
      styles={colourStyles}
      formatGroupLabel={formatGroupLabel}
      value={props.value}
      closeMenuOnSelect={false}
    />
  );
};
